import { apiService, API_ENDPOINTS, buildEndpoint } from "@/shared/services/api";
import type { APIResponse } from "@/shared/types";

// Types for HR Staff functionality
export interface RecruitMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: string;
  status: 'active' | 'inactive';
  salesCount: number;
  lastSaleDate?: string;
  recruitCount: number;
  currentFunnelStep: number;
  stepName: string;
  joinedAt: string;
  performance: 'overperforming' | 'normal' | 'inactive';
  referrerId?: string;
  referrerName?: string;
  organizationId?: string;
  organizationName?: string;
}

export interface FunnelStat {
  step: number;
  name: string;
  count: number;
  percentage: number;
  color: string;
}

export interface AllRecruitsData {
  totalRecruits: number;
  activeRecruits: number;
  inactiveRecruits: number;
  funnelStats: FunnelStat[];
  recruits: RecruitMember[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface RecruitFilters {
  step?: number;
  status?: 'active' | 'inactive';
  organizationId?: string;
  referrerId?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface OrganizationDownlineNode {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  organizationId?: string;
  organizationName?: string;
  organizationType?: string;
  teamSize: number;
  totalSales: number;
  approvalRating: number;
  lastSaleDate?: string;
  performance: 'overperforming' | 'normal' | 'inactive';
  children: OrganizationDownlineNode[];
}

export interface OrganizationTreeResponse {
  tree: OrganizationDownlineNode | null;
  totalTeamSize: number;
  totalSales: number;
  averageApprovalRating: number;
}

export interface RecruitApplication {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  currentFunnelStep: number;
  stepName: string;
  funnelProgress: {
    step1CompletedAt?: string;
    step2CompletedAt?: string;
    step3CompletedAt?: string;
    step4CompletedAt?: string;
    step5CompletedAt?: string;
    step6CompletedAt?: string;
    step7CompletedAt?: string;
    step8CompletedAt?: string;
  };
  trainingSchedule?: {
    id: string;
    status: string;
    preferredSlot1: string;
    preferredSlot2?: string;
    preferredSlot3?: string;
    confirmedSlot?: string;
    completedAt?: string;
  };
  testAttempts: {
    id: string;
    testName: string;
    score: number;
    passed: boolean;
    attemptedAt: string;
    completedAt?: string;
  }[];
  documents: {
    id: string;
    name: string;
    type: string;
    status: string;
    signedAt?: string;
    signedDocumentUrl?: string;
  }[];
  taxDocuments: {
    id: string;
    name: string;
    type: string;
    status: string;
    signedAt?: string;
    signedDocumentUrl?: string;
  }[];
  referrer?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  organization?: {
    id: string;
    name: string;
    type: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface AgentCampaignStatus {
  agentId: string;
  agentName: string;
  agentEmail: string;
  isActive: boolean;
  canSell: boolean;
  status: 'active' | 'deactivated' | 'terminated';
  campaignDeactivations: {
    campaignId: string;
    campaignName: string;
    deactivatedAt: string;
    reason: string;
    deactivatedBy: string;
  }[];
  statusHistory: {
    id: string;
    action: string;
    previousStatus?: string;
    newStatus?: string;
    reason: string;
    performedBy: string;
    performedAt: string;
    notes?: string;
  }[];
}

export interface AgentCampaign {
  id: string;
  name: string;
  isActive: boolean;
}

export class HRStaffService {
  // ==========================================
  // SECTION 3.1: Recruit Tracking
  // ==========================================

  /**
   * Get all recruits across organization with 8-step funnel tracking
   */
  static async getAllRecruits(filters?: RecruitFilters): Promise<AllRecruitsData> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const response = await apiService.get<APIResponse<AllRecruitsData>>(
      `${API_ENDPOINTS.hrStaff.recruits}?${params.toString()}`
    );
    return response.data.data!;
  }

  // ==========================================
  // SECTION 3.2: Downline Tree Visualization
  // ==========================================

  /**
   * Get full organization hierarchy tree
   */
  static async getOrganizationTree(rootUserId?: string, depth?: number): Promise<OrganizationTreeResponse> {
    const params = new URLSearchParams();
    if (rootUserId) params.append('rootUserId', rootUserId);
    if (depth) params.append('depth', String(depth));

    const response = await apiService.get<APIResponse<OrganizationTreeResponse>>(
      `${API_ENDPOINTS.hrStaff.organizationTree}?${params.toString()}`
    );
    return response.data.data!;
  }

  // ==========================================
  // SECTION 3.3: Recruit Management Panel
  // ==========================================

  /**
   * Get full recruit application details
   */
  static async getRecruitApplication(recruitId: string): Promise<RecruitApplication> {
    const response = await apiService.get<APIResponse<RecruitApplication>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.recruitApplication, { recruitId })
    );
    return response.data.data!;
  }

  /**
   * Approve or deny a recruit to proceed through onboarding
   */
  static async updateRecruitApproval(
    recruitId: string,
    data: {
      action: 'approve' | 'deny' | 'flag';
      targetStep?: number;
      reason?: string;
      notes?: string;
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiService.post<APIResponse<{ success: boolean; message: string }>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.recruitApproval, { recruitId }),
      data
    );
    return response.data.data!;
  }

  // ==========================================
  // SECTION 3.4: Granular Deactivation Controls
  // ==========================================

  /**
   * Toggle agent's ability to do sales (while keeping login active)
   */
  static async toggleAgentSalesAbility(
    agentId: string,
    canSell: boolean,
    reason: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiService.put<APIResponse<{ success: boolean; message: string }>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.agentSalesAbility, { agentId }),
      { canSell, reason, notes }
    );
    return response.data.data!;
  }

  /**
   * Get agent's full status including campaign deactivations
   */
  static async getAgentCampaignStatus(agentId: string): Promise<AgentCampaignStatus> {
    const response = await apiService.get<APIResponse<AgentCampaignStatus>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.agentCampaignStatus, { agentId })
    );
    return response.data.data!;
  }

  /**
   * Get all campaigns an agent is active for
   */
  static async getAgentActiveCampaigns(agentId: string): Promise<AgentCampaign[]> {
    const response = await apiService.get<APIResponse<AgentCampaign[]>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.agentActiveCampaigns, { agentId })
    );
    return response.data.data!;
  }

  /**
   * Get agent status history
   */
  static async getAgentStatusHistory(
    agentId: string,
    page?: number,
    limit?: number
  ): Promise<{
    history: any[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (page) params.append('page', String(page));
    if (limit) params.append('limit', String(limit));

    const response = await apiService.get<APIResponse<{
      history: any[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>>(
      `${buildEndpoint(API_ENDPOINTS.hrStaff.agentStatusHistory, { agentId })}?${params.toString()}`
    );
    return response.data.data!;
  }

  /**
   * Deactivate agent for a specific campaign
   */
  static async deactivateAgentForCampaign(
    agentId: string,
    campaignId: string,
    reason: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiService.post<APIResponse<{ success: boolean; message: string }>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.deactivateForCampaign, { agentId, campaignId }),
      { reason, notes }
    );
    return response.data.data!;
  }

  /**
   * Reactivate agent for a specific campaign
   */
  static async reactivateAgentForCampaign(
    agentId: string,
    campaignId: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiService.post<APIResponse<{ success: boolean; message: string }>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.reactivateForCampaign, { agentId, campaignId }),
      { notes }
    );
    return response.data.data!;
  }

  /**
   * Deactivate agent with reason tracking (enhanced version)
   */
  static async deactivateAgentWithReason(
    agentId: string,
    reason: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiService.post<APIResponse<{ success: boolean; message: string }>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.deactivateWithReason, { agentId }),
      { reason, notes }
    );
    return response.data.data!;
  }

  /**
   * Activate agent with reason tracking (enhanced version)
   */
  static async activateAgentWithReason(
    agentId: string,
    reason: string,
    notes?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await apiService.post<APIResponse<{ success: boolean; message: string }>>(
      buildEndpoint(API_ENDPOINTS.hrStaff.activateWithReason, { agentId }),
      { reason, notes }
    );
    return response.data.data!;
  }
}
